import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useMemo, useState } from 'react';
import { Search, Package, X, ArrowLeftRight } from 'lucide-react';
import ShimmerImage from '../../../components/ShimmerImage';
import { code128Geometry } from '../../../utils/code128';
import {
  UNIT_STATUS_LABEL, UNIT_STATUS_CHIP, type DeviceModelRow, type DeviceUnitRow,
} from '../../../services/hardwareApi';
import { unitBadgeLabel, worstBadgeSeverity } from '../../inventory/unitBadgeSafe';
import { hardwareApi } from '../../../services/hardwareApi';
import { toastService } from '../../../services/toastService';
import OverrideGate from './OverrideGate';
import { RubyLoader } from '../../../components/RubyLoader';

/**
 * PICKING A PHYSICAL DEVICE, BY LOOKING AT IT.
 *
 * This was a `SearchableDropdown` of one-line labels. Choosing which box on a
 * shelf a customer receives is not a form field: the operator is holding a
 * device, and the only things that identify it are its picture, its serial and
 * the barcode printed on it. A dropdown shows none of them.
 *
 * The selected unit stays on screen as a full card rather than collapsing to a
 * line, because it is the thing every step below is about.
 */

const Barcode: React.FC<{ value: string; width: number; height: number }> = ({ value, width, height }) => {
  const { bars, modules } = useMemo(() => code128Geometry(value), [value]);
  if (!modules) return null;
  return (
    <svg viewBox={`0 0 ${modules} ${height}`} width={width} height={height} preserveAspectRatio="none"
      role="img" aria-label={`Barcode ${value}`} style={{ display: 'block' }}>
      {bars.map((b, i) => <rect key={i} x={b.x} y={0} width={b.width} height={height} fill="#1C1917" />)}
    </svg>
  );
};

const BADGE_CLASS: Record<string, string> = {
  good: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  watch: 'border-amber-200 bg-amber-50 text-amber-700',
  fault: 'border-red-200 bg-[#FFF1F3] text-[#9F1239]',
};

const Badges: React.FC<{ ids?: string[] }> = ({ ids }) => (
  <>
    {(ids || []).map((b) => (
      <span key={b} className={`rounded-full border px-1.5 py-px text-[9px] font-bold uppercase tracking-wide ${
        BADGE_CLASS[worstBadgeSeverity([b]) || 'good']
      }`}>{unitBadgeLabel(b)}</span>
    ))}
  </>
);

const Thumb: React.FC<{ model?: DeviceModelRow; size: number }> = ({ model, size }) => (
  <ShimmerImage
    src={model?.media?.heroImageUrl}
    alt={model?.modelName || 'Device'}
    width={size}
    height={size}
    objectFit="contain"
    variant="light"
    className="shrink-0 rounded-lg border border-stone-200 bg-stone-50"
    fallbackBgColor="bg-stone-50"
    fallbackIcon={<Package size={Math.round(size * 0.42)} className="text-stone-300" />}
  />
);

const MAX_RESULTS = 8;

/**
 * The searchable list of device cards.
 *
 * Shared by the first pick and by a swap, so the two can never drift into
 * showing different things about the same hardware — which is exactly the
 * mistake that would let someone swap on less information than they chose on.
 */
const UnitList: React.FC<{
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  q: string;
  setQ: (v: string) => void;
  loading?: boolean;
  onPick: (unitId: string) => void;
}> = ({ units, models, q, setQ, loading, onPick }) => {
  const modelOf = (id?: string) => models.find((m) => m.modelId === id);
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = s
      ? units.filter((u) =>
        (u.trackingId || '').toLowerCase().includes(s)
        || (u.serialNumber || '').toLowerCase().includes(s)
        || (models.find((m) => m.modelId === u.modelId)?.modelName || '').toLowerCase().includes(s))
      : units;
    return base.slice(0, MAX_RESULTS);
  }, [units, models, q]);

  return (
    <div>
      <div className="flex min-h-[46px] items-center gap-2 rounded-lg border-2 border-[#DC143C] bg-white px-3">
        <Search size={16} className="shrink-0 text-[#DC143C]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={loading}
          placeholder={loading ? 'Loading units…' : 'Scan or type a tracking ID, serial or model…'}
          aria-label="Find a unit"
          className="flex-1 border-0 bg-transparent py-2.5 text-[13.5px] outline-none placeholder:text-stone-400"
        />
      </div>

      <div className="mt-1.5 overflow-hidden rounded-lg border border-stone-200 bg-white">
        {loading ? (
          <RubyLoader variant="section" label="Loading units…" />
        ) : hits.length === 0 ? (
          <p className="px-3 py-6 text-center text-[13px] text-stone-500">
            {q ? <>Nothing matches <b className="text-stone-800">{q}</b>.</> : 'No units available.'}
          </p>
        ) : hits.map((u) => {
          const m = modelOf(u.modelId);
          const label = u.trackingId || u.unitId;
          return (
            <button
              key={u.unitId}
              type="button"
              onClick={() => onPick(u.unitId)}
              className="grid w-full grid-cols-[52px_1fr_auto] items-center gap-3 border-b border-stone-100 px-3 py-2.5 text-left last:border-b-0 hover:bg-[#FFF1F3]"
            >
              <Thumb model={m} size={52} />
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[13.5px] font-semibold text-stone-900">{label}</span>
                  <span className={`rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold ${UNIT_STATUS_CHIP[u.state]}`}>
                    {UNIT_STATUS_LABEL[u.state]}
                  </span>
                  <Badges ids={u.badges} />
                </span>
                <span className="mt-0.5 block truncate text-[12px] text-stone-600">{m?.modelName || u.modelId}</span>
                {u.serialNumber && (
                  <span className="mt-0.5 block break-all font-mono text-[10.5px] text-stone-400">
                    serial {u.serialNumber}
                  </span>
                )}
              </span>
              <span className="flex flex-col items-center gap-[3px]">
                <Barcode value={label} width={110} height={22} />
                <span className="font-mono text-[9px] tracking-[0.08em] text-stone-500">{label}</span>
              </span>
            </button>
          );
        })}
      </div>

      {!loading && units.length > hits.length && !q && (
        <p className="mt-1.5 text-center text-[11px] text-stone-400">
          Showing {hits.length} of {units.length} — type to narrow.
        </p>
      )}
    </div>
  );
};


const UnitPickerCards: React.FC<{
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  value: string;
  loading?: boolean;
  onChange: (unitId: string | null) => void;
  /** Called after a live-order swap lands, so the caller can refetch. */
  onSwapped?: () => void;
}> = ({ units, models, value, loading, onChange, onSwapped }) => {
  const [q, setQ] = useState('');
  /**
   * Changing the device on a LIVE ORDER is a swap, not a re-selection.
   *
   *   locked   — the card is shown, changing it needs a code
   *   unlocked — the list is back, and picking one performs the swap
   *
   * A unit with no order is just a browsing choice and needs no ceremony.
   */
  const [swapStage, setSwapStage] = useState<'locked' | 'unlocked'>('locked');
  const [swapping, setSwapping] = useState(false);
  /**
   * SWAP = release the old, then allocate the new to the same order.
   *
   * Two calls rather than one, because `allocateOrder` is the ONLY allocation
   * path by design — a bespoke "put exactly this unit on that order" would have
   * to re-implement every guard it holds, and the one it got wrong would be the
   * one that double-allocates a device. Releasing first leaves the order short
   * by one, so allocating with the new unit PREFERRED fills that gap with it.
   *
   * The failure between the two is reported honestly rather than swallowed: if
   * the release lands and the allocation does not, the old device is on the
   * shelf and the order is short — recoverable, visible, and the operator is
   * told exactly that instead of being shown a success.
   */
  const swapTo = async (nextUnitId: string) => {
    if (!selected?.orderId) { onChange(nextUnitId); return; }
    const orderId = selected.orderId;
    setSwapping(true);
    try {
      await hardwareApi.releaseUnit(selected.unitId);
    } catch (e: any) {
      toastService.error(e?.message || 'Could not release the current device — nothing changed.');
      setSwapping(false);
      return;
    }
    try {
      await hardwareApi.allocateOrder(orderId, [nextUnitId]);
      toastService.success('Device swapped. The previous one is back in stock.');
      onChange(nextUnitId);
      setSwapStage('locked');
      onSwapped?.();
    } catch (e: any) {
      toastService.error(
        `The old device was released but the new one could not be allocated: ${e?.message || 'unknown error'}. `
        + 'The order is now short by one — allocate it again from the order.',
      );
      onChange(null);
      setSwapStage('locked');
      onSwapped?.();
    } finally {
      setSwapping(false);
    }
  };

  const modelOf = (id?: string) => models.find((m) => m.modelId === id);
  const selected = units.find((u) => u.unitId === value) || null;

  // The chosen device stays fully visible. Collapsing it to a line would hide
  // the serial and barcode at exactly the moment they are being checked
  // against the hardware in front of the operator.
  if (selected) {
    const m = modelOf(selected.modelId);
    const label = selected.trackingId || selected.unitId;
    return (
      <div className="rounded-xl border-2 border-[#DC143C] bg-white p-3">
        <div className="flex items-start gap-3">
          <Thumb model={m} size={72} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[15px] font-bold text-stone-900">{label}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${UNIT_STATUS_CHIP[selected.state]}`}>
                {UNIT_STATUS_LABEL[selected.state]}
              </span>
              <Badges ids={selected.badges} />
            </div>
            <div className="mt-0.5 text-[12.5px] text-stone-600">{m?.modelName || selected.modelId}</div>
            <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11.5px]">
              {selected.serialNumber && (
                <>
                  <dt className="font-bold uppercase tracking-wide text-stone-400">Serial</dt>
                  <dd className="break-all font-mono text-stone-700">{selected.serialNumber}</dd>
                </>
              )}
              {selected.orderId && (
                <>
                  <dt className="font-bold uppercase tracking-wide text-stone-400">Order</dt>
                  <dd className="font-mono text-stone-700">{selected.orderId.replace(/^ord_/, '').slice(0, 8).toUpperCase()}</dd>
                </>
              )}
            </dl>
            {(selected.notes || '').trim() && (
              <p className="mt-1.5 truncate text-[11.5px] italic text-stone-500">“{selected.notes}”</p>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-center gap-2">
            <div className="flex flex-col items-center gap-[3px] rounded-lg border border-stone-200 bg-white p-1.5">
              <Barcode value={label} width={150} height={34} />
              <span className="font-mono text-[9.5px] tracking-[0.1em] text-stone-700">{label}</span>
            </div>
            {/* A unit with no order is a browsing choice; one ON an order is a
                customer's device, and changing it is the thing the code guards. */}
            {!selected.orderId && (
              <button
                onClick={() => { onChange(null); setQ(''); }}
                className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-stone-500 hover:text-[#DC143C]"
              >
                <X size={12} /> Pick another
              </button>
            )}
          </div>
        </div>

        {selected.orderId && (
          <div className="mt-3 border-t border-stone-100 pt-3">
            {swapStage === 'locked' ? (
              <OverrideGate
                unitId={selected.unitId}
                intent="Changing the device on this order"
                onUnlocked={() => { setSwapStage('unlocked'); setQ(''); }}
              />
            ) : (
              <p className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-[11.5px] font-medium text-emerald-800">
                <ArrowLeftRight size={12} />
                Unlocked — pick a replacement below. {label} returns to stock automatically.
              </p>
            )}
          </div>
        )}

        {selected.orderId && swapStage === 'unlocked' && (
          <div className="mt-3">
            {swapping ? (
              <p className="flex items-center justify-center gap-2 py-6 text-[13px] text-stone-500">
                <RubyGemMark size={14} drawing /> Swapping…
              </p>
            ) : (
              <UnitList
                units={units.filter((u) => u.unitId !== selected.unitId)}
                models={models}
                q={q}
                setQ={setQ}
                loading={loading}
                onPick={swapTo}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <UnitList
      units={units}
      models={models}
      q={q}
      setQ={setQ}
      loading={loading}
      onPick={(id) => onChange(id)}
    />
  );
};

export default UnitPickerCards;
